import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Route as RouteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { deviceService } from '../services/deviceService';

const DeviceRegistrationDialog = ({ 
  open, 
  onClose, 
  organizationData, 
  currentUser, 
  onSuccess, 
  onError 
}) => {
  const [deviceUuid, setDeviceUuid] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [selectedNetwork, setSelectedNetwork] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [registering, setRegistering] = useState(false);

  // New streaming fields
  const [streamUrl, setStreamUrl] = useState('');
  const [streamNetworkId, setStreamNetworkId] = useState('');
  const [streamSectionId, setStreamSectionId] = useState('');

  const resetForm = () => {
    setDeviceUuid('');
    setDeviceName('');
    setSelectedNetwork('');
    setVehicleId('');
    // Reset streaming fields
    setStreamUrl('');
    setStreamNetworkId('');
    setStreamSectionId('');
  };

  const handleClose = () => {
    if (!registering) {
      resetForm();
      onClose();
    }
  };

  // UUID validation function
  const isValidUUID = (uuid) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  };

  const handleSubmit = async () => {
    // Note: selectedNetwork can be an empty string for org-wide, so we don't validate its presence here.
    if (!deviceUuid.trim() || !deviceName.trim() || !vehicleId.trim()) {
      onError('Please fill in all required fields');
      return;
    }

    // Validate UUID format
    if (!isValidUUID(deviceUuid.trim())) {
      onError('Invalid UUID format. Please enter a valid UUID (e.g., 838987c0-0045-45c3-ae44-0012ac19da62)');
      return;
    }

    // Validate streaming fields
    if (streamUrl.trim()) {
      if (!streamNetworkId) {
        onError('Stream network is required when stream URL is provided');
        return;
      }
      // Validate URL format
      if (!streamUrl.match(/^https?:\/\/.+/)) {
        onError('Stream URL must be a valid HTTP or HTTPS URL');
        return;
      }
    }

    setRegistering(true);
    try {
      const deviceData = {
        device_uuid: deviceUuid.trim(),
        device_name: deviceName.trim(),
        organization_id: currentUser.organizationId,
        network_id: selectedNetwork === 'org-wide' ? null : selectedNetwork,
        vehicle_id: vehicleId.trim(),
        created_by: currentUser.id
      };

      // Add streaming fields if provided
      if (streamUrl.trim()) {
        deviceData.stream_url = streamUrl.trim();
        deviceData.stream_network_id = streamNetworkId;
        if (streamSectionId) {
          deviceData.stream_section_id = streamSectionId;
        }
      }

      await deviceService.registerDevice(deviceData);

      onSuccess('Device registered and bound successfully!');
      resetForm();
      onClose();
    } catch (err) {
      onError(err.message);
    } finally {
      setRegistering(false);
    }
  };

  const isFormValid = deviceUuid.trim() &&
                     deviceName.trim() &&
                     vehicleId.trim() &&
                     isValidUUID(deviceUuid.trim());

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <SettingsIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Register New Device
          </Typography>
        </Stack>
      </DialogTitle>
      
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="Device UUID"
            value={deviceUuid}
            onChange={(e) => setDeviceUuid(e.target.value)}
            placeholder="e.g., 838987c0-0045-45c3-ae44-0012ac19da62"
            fullWidth
            required
            helperText={
              deviceUuid && !isValidUUID(deviceUuid) 
                ? "Invalid UUID format. Must be in format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                : "Enter the unique identifier for the ENViS device (printed on device sticker)"
            }
            error={deviceUuid && !isValidUUID(deviceUuid)}
            disabled={registering}
          />
          
          <TextField
            label="Device Name"
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
            placeholder="Enter device name"
            fullWidth
            required
            helperText="User-friendly name for the device"
            disabled={registering}
          />
          
          <TextField
            label="Vehicle ID"
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
            placeholder="Enter vehicle identifier (e.g., license plate)"
            fullWidth
            required
            helperText="Identifier of the vehicle where the device is mounted"
            disabled={registering}
          />
          
          <FormControl fullWidth required disabled={registering}>
            <InputLabel>Network</InputLabel>
            <Select
              value={selectedNetwork}
              onChange={(e) => setSelectedNetwork(e.target.value)}
              label="Network"
              renderValue={(selected) => {
                if (selected === 'org-wide') {
                  return <em>All Networks (Organization-Wide)</em>;
                }
                const network = organizationData?.networks?.find(n => n.id === selected);
                return network ? network.name : '';
              }}
            >
              <MenuItem value="org-wide">
                <em>All Networks (Organization-Wide)</em>
              </MenuItem>
              {organizationData?.networks?.map((network) => (
                <MenuItem key={network.id} value={network.id}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <RouteIcon fontSize="small" />
                    <Box>
                      <Typography variant="body2">{network.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {network.code} • {network.deviceCount} devices
                      </Typography>
                    </Box>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Live Streaming Section */}
          <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom color="primary">
              🔴 Live Streaming (Optional)
            </Typography>
            
            <Stack spacing={2}>
              <TextField
                label="Stream URL"
                value={streamUrl}
                onChange={(e) => setStreamUrl(e.target.value)}
                placeholder="https://stream-url.example.com/video"
                fullWidth
                helperText="HTTP/HTTPS URL for live video stream from this device"
                disabled={registering}
              />
              
              {streamUrl.trim() && (
                <>
                  <FormControl fullWidth required disabled={registering}>
                    <InputLabel>Stream Network</InputLabel>
                    <Select
                      value={streamNetworkId}
                      onChange={(e) => setStreamNetworkId(e.target.value)}
                      label="Stream Network"
                    >
                      {organizationData?.networks?.map((network) => (
                        <MenuItem key={network.id} value={network.id}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <RouteIcon fontSize="small" />
                            <Box>
                              <Typography variant="body2">{network.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {network.code}
                              </Typography>
                            </Box>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  {streamNetworkId && (
                    <FormControl fullWidth disabled={registering}>
                      <InputLabel>Stream Section (Optional)</InputLabel>
                      <Select
                        value={streamSectionId}
                        onChange={(e) => setStreamSectionId(e.target.value)}
                        label="Stream Section (Optional)"
                      >
                        <MenuItem value="">
                          <em>No specific section</em>
                        </MenuItem>
                        {(() => {
                          const selectedNetwork = organizationData?.networks?.find(n => n.id === streamNetworkId);
                          console.log('Selected network for sections:', selectedNetwork);
                          console.log('Available sections:', selectedNetwork?.sections);
                          return selectedNetwork?.sections?.map((section) => (
                            <MenuItem key={section.id} value={section.id}>
                              <Typography variant="body2">{section.name}</Typography>
                            </MenuItem>
                          ));
                        })()}
                      </Select>
                    </FormControl>
                  )}
                </>
              )}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ p: 3 }}>
        <Button 
          onClick={handleClose}
          disabled={registering}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={registering || !isFormValid}
          startIcon={registering ? <CircularProgress size={16} /> : <AddIcon />}
        >
          {registering ? 'Registering...' : 'Register Device'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceRegistrationDialog; 